import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAuthLibraryTheme } from '../hooks';
import { useTranslation, ACTIVE_LANGUAGES, languageLabels } from '../lib/i18n';
import { ACCOUNT_URL, LOOM_URL, COOKIE_DOMAIN } from '../lib/config';
import api from '../lib/api';

function AppSidebar({ collapsed, onToggleCollapse, sidebarOpen, onCloseSidebar }) {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, signOut, getToken } = useAuth();
  const { theme: authLibraryTheme, toggleTheme: toggleAuthLibraryTheme } = useAuthLibraryTheme();
  const { t, language, setLanguage } = useTranslation();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showLangSubmenu, setShowLangSubmenu] = useState(false);
  const [menuPos, setMenuPos] = useState(null);
  const triggerRef = useRef(null);

  const isPaid = user?.plan !== 'free' && !!user?.plan;

  // Compute fixed position for the user menu when it opens
  useEffect(() => {
    if (showUserMenu && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuPos({ bottom: window.innerHeight - rect.top + 6, left: rect.left, width: rect.width });
    }
  }, [showUserMenu]);

  const handleSignOut = useCallback(async () => {
    await signOut();
    navigate('/auth');
  }, [signOut, navigate]);

  const handleLanguageChange = useCallback((code) => {
    setLanguage(code);
    setShowLangSubmenu(false);
    setShowUserMenu(false);
    if (getToken()) {
      api.updateSettings({ locale: code }).catch(() => {});
    }
  }, [setLanguage, getToken]);

  const handleNavClick = useCallback(() => {
    // Close mobile sidebar on navigation
    if (sidebarOpen) onCloseSidebar();
  }, [sidebarOpen, onCloseSidebar]);

  // Active state detection
  const viewParam = searchParams.get('view');
  const filterParam = searchParams.get('filter');
  const isHome = location.pathname === '/' && !viewParam;
  const isAllBooks = location.pathname === '/' && viewParam === 'all-books' && !filterParam;
  const isReading = location.pathname === '/' && viewParam === 'all-books' && filterParam === 'reading';
  const isFinished = location.pathname === '/' && viewParam === 'all-books' && filterParam === 'finished';
  const isCurated = location.pathname === '/' && viewParam === 'curated-library';
  // Curated library is split into per-language shelves (?clang=en|fr).
  // A bare ?view=curated-library (old bookmark, no clang) defaults to English.
  const curatedLang = (searchParams.get('clang') || '').toLowerCase();
  const isCuratedEn = isCurated && curatedLang !== 'fr';
  const isCuratedFr = isCurated && curatedLang === 'fr';
  const isAnalytics = location.pathname === '/dashboard';

  return (
    <>
      {/* Mobile backdrop */}
      <div
        className={`app-sidebar-backdrop ${sidebarOpen ? 'visible' : ''}`}
        onClick={onCloseSidebar}
      />

      <aside className={`app-sidebar ${collapsed ? 'collapsed' : ''} ${sidebarOpen ? 'mobile-open' : ''}`}>
        {/* Brand + product nav */}
        <div className="app-sidebar-brand-section">
          <button
            type="button"
            className="app-sidebar-brand"
            onClick={() => navigate('/')}
          >
            <img src="/EaseVerse_Reader_ClassicBlue_Depth_v3.jpg" alt="" className="app-sidebar-brand-logo" />
            {!collapsed && <h1 className="app-sidebar-brand-title">EaseVerse</h1>}
          </button>
          {!collapsed && (
            <nav className="app-sidebar-product-nav">
              <a
                href="https://chromewebstore.google.com/search/easeverse"
                target="_blank"
                rel="noopener noreferrer"
                className="app-sidebar-product-tab"
              >
                Lens
              </a>
              <a href="/" className="app-sidebar-product-tab active">
                Reader
              </a>
              <a
                href={`${LOOM_URL}?source=reader`}
                className="app-sidebar-product-tab"
                onClick={() => {
                  const tk = getToken?.();
                  if (tk) {
                    document.cookie = `ev_account_token=${encodeURIComponent(tk)};${COOKIE_DOMAIN ? ` domain=${COOKIE_DOMAIN};` : ''} path=/; max-age=60; Secure; SameSite=Lax`;
                  }
                }}
              >
                Loom
              </a>
            </nav>
          )}
        </div>

        <nav className="app-sidebar-nav">
          {/* Search */}
          <div className="app-sidebar-search">
            {collapsed ? (
              <button
                className="app-sidebar-item"
                onClick={() => { navigate('/'); onToggleCollapse(); }}
                title={t('sidebar.search')}
              >
                <span className="app-sidebar-item-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                </span>
              </button>
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleNavClick();
                }}
              >
                <div className="app-sidebar-search-wrap">
                  <svg className="app-sidebar-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                  <input
                    name="q"
                    type="text"
                    value={searchParams.get('q') || ''}
                    placeholder={t('sidebar.searchPlaceholder')}
                    className="app-sidebar-search-input"
                    onChange={(e) => {
                      const val = e.target.value.trim();
                      navigate(val ? `/?q=${encodeURIComponent(val)}` : '/', { replace: true });
                    }}
                  />
                </div>
              </form>
            )}
          </div>

          {/* Home */}
          <button
            className={`app-sidebar-item ${isHome ? 'active' : ''}`}
            onClick={() => { navigate('/'); handleNavClick(); }}
            title={collapsed ? t('sidebar.home') : undefined}
          >
            <span className="app-sidebar-item-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                <polyline points="9 22 9 12 15 12 15 22" />
              </svg>
            </span>
            <span className="app-sidebar-item-text">{t('sidebar.home')}</span>
          </button>

          {/* My Bookshelf section */}
          {!collapsed && <div className="app-sidebar-label app-sidebar-label-spaced">{t('sidebar.myBookshelf')}</div>}
          <button
            className={`app-sidebar-item ${isAllBooks ? 'active' : ''}`}
            onClick={() => { navigate('/?view=all-books'); handleNavClick(); }}
            title={collapsed ? t('sidebar.allBooks') : undefined}
          >
            <span className="app-sidebar-item-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
              </svg>
            </span>
            <span className="app-sidebar-item-text">{t('sidebar.allBooks')}</span>
          </button>
          <button
            className={`app-sidebar-item ${isReading ? 'active' : ''}`}
            onClick={() => { navigate('/?view=all-books&filter=reading'); handleNavClick(); }}
            title={collapsed ? t('sidebar.reading') : undefined}
          >
            <span className="app-sidebar-item-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
                <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
              </svg>
            </span>
            <span className="app-sidebar-item-text">{t('sidebar.reading')}</span>
          </button>
          <button
            className={`app-sidebar-item ${isFinished ? 'active' : ''}`}
            onClick={() => { navigate('/?view=all-books&filter=finished'); handleNavClick(); }}
            title={collapsed ? t('sidebar.finished') : undefined}
          >
            <span className="app-sidebar-item-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
            </span>
            <span className="app-sidebar-item-text">{t('sidebar.finished')}</span>
          </button>

          {/* Curated Library section — split into per-language shelves */}
          {!collapsed && <div className="app-sidebar-label app-sidebar-label-spaced">{t('sidebar.curatedLibrary')}</div>}
          <button
            className={`app-sidebar-item ${isCuratedEn ? 'active' : ''}`}
            onClick={() => { navigate('/?view=curated-library&clang=en'); handleNavClick(); }}
            title={collapsed ? t('library.curatedEnglish') : undefined}
          >
            <span className="app-sidebar-item-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
              </svg>
            </span>
            <span className="app-sidebar-item-text">{t('library.curatedEnglish')}</span>
          </button>
          <button
            className={`app-sidebar-item ${isCuratedFr ? 'active' : ''}`}
            onClick={() => { navigate('/?view=curated-library&clang=fr'); handleNavClick(); }}
            title={collapsed ? t('library.curatedFrench') : undefined}
          >
            <span className="app-sidebar-item-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
              </svg>
            </span>
            <span className="app-sidebar-item-text">{t('library.curatedFrench')}</span>
          </button>

          {/* Reading Analytics section */}
          {!collapsed && <div className="app-sidebar-label app-sidebar-label-spaced">{t('sidebar.readingAnalytics')}</div>}
          <button
            className={`app-sidebar-item ${isAnalytics ? 'active' : ''}`}
            onClick={() => { navigate('/dashboard'); handleNavClick(); }}
            title={collapsed ? t('sidebar.readingAnalytics') : undefined}
          >
            <span className="app-sidebar-item-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="20" x2="18" y2="10" />
                <line x1="12" y1="20" x2="12" y2="4" />
                <line x1="6" y1="20" x2="6" y2="14" />
              </svg>
            </span>
            <span className="app-sidebar-item-text">{t('sidebar.readingAnalytics')}</span>
          </button>

          {/* Spacer */}
          <div className="app-sidebar-spacer" />
        </nav>

        {/* Bottom: User trigger + popover */}
        <div className="app-sidebar-bottom">
          <div className="app-sidebar-user-trigger-wrap">
            {/* User popover menu (fixed position to escape overflow clipping) */}
            {showUserMenu && menuPos && (
              <>
                <div className="app-sidebar-user-menu-backdrop" onClick={() => { setShowUserMenu(false); setShowLangSubmenu(false); }} />
                <div className="app-sidebar-user-menu" style={{ position: 'fixed', bottom: menuPos.bottom, left: menuPos.left, width: Math.max(menuPos.width, 220) }}>
                  {/* User info header */}
                  <div className="app-sidebar-menu-header">
                    <span className="app-sidebar-menu-email">{user?.email}</span>
                  </div>
                  <div className="app-sidebar-menu-divider" />

                  {/* Language — has submenu */}
                  <div className="app-sidebar-menu-item-wrap">
                    <button
                      className="app-sidebar-menu-item"
                      onClick={() => setShowLangSubmenu(!showLangSubmenu)}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" />
                        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                      </svg>
                      <span>{languageLabels[language]}</span>
                      <svg className="app-sidebar-menu-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
                    </button>
                    {showLangSubmenu && (
                      <div className="app-sidebar-lang-submenu">
                        {ACTIVE_LANGUAGES.map((code) => (
                          <button
                            key={code}
                            className={`app-sidebar-lang-item ${language === code ? 'active' : ''}`}
                            onClick={() => handleLanguageChange(code)}
                          >
                            {language === code ? (
                              <svg className="app-sidebar-lang-check" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                            ) : (
                              <span className="app-sidebar-lang-spacer" />
                            )}
                            {languageLabels[code]}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Theme toggle */}
                  <button
                    className="app-sidebar-menu-item"
                    onClick={() => { toggleAuthLibraryTheme(); setShowUserMenu(false); }}
                  >
                    {authLibraryTheme === 'light' ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" /></svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="5" /><path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" /></svg>
                    )}
                    <span>{authLibraryTheme === 'light' ? t('sidebar.darkMode') : t('sidebar.lightMode')}</span>
                  </button>

                  {/* Account center */}
                  <a
                    className="app-sidebar-menu-item"
                    href={`${ACCOUNT_URL}?source=reader`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => {
                      const tk = getToken?.();
                      if (tk) {
                        document.cookie = `ev_account_token=${encodeURIComponent(tk)};${COOKIE_DOMAIN ? ` domain=${COOKIE_DOMAIN};` : ''} path=/; max-age=60; Secure; SameSite=Lax`;
                      }
                      setShowUserMenu(false);
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                    </svg>
                    <span>{t('sidebar.settings')}</span>
                  </a>

                  <div className="app-sidebar-menu-divider" />

                  {/* Sign out */}
                  <button
                    className="app-sidebar-menu-item app-sidebar-menu-item-danger"
                    onClick={() => { setShowUserMenu(false); handleSignOut(); }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" />
                    </svg>
                    <span>{t('library.signOut')}</span>
                  </button>
                </div>
              </>
            )}

            {/* User trigger button */}
            <button
              ref={triggerRef}
              className="app-sidebar-user-trigger"
              onClick={() => { setShowUserMenu(!showUserMenu); setShowLangSubmenu(false); }}
              title={collapsed ? (user?.display_name || user?.email) : undefined}
            >
              <span className="app-sidebar-account-avatar">
                <span className="app-sidebar-avatar-initials">
                  {(user?.display_name || user?.email || '?').slice(0, 2).toUpperCase()}
                </span>
              </span>
              <span className="app-sidebar-user-trigger-info">
                <span className="app-sidebar-user-trigger-name">{user?.display_name || user?.email}</span>
                {/* Product badge: prefer the backend-provided localized product
                    name (e.g. {zh:'基础版',en:'Basic'}; Lens-only subscribers
                    get 'Lens'); fall back to the legacy plan→label mapping for
                    old backends / stale caches that lack productName. */}
                {isPaid && <span className="app-sidebar-user-trigger-plan">{user?.productName?.[language] ?? user?.productName?.en ?? (user?.plan === 'pro' ? 'Pro' : user?.plan === 'max' ? 'Max' : user?.plan === 'standard' ? 'Basic' : '')}</span>}
              </span>
              <svg className="app-sidebar-user-trigger-dots" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <circle cx="12" cy="5" r="1.5" /><circle cx="12" cy="12" r="1.5" /><circle cx="12" cy="19" r="1.5" />
              </svg>
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}

export default React.memo(AppSidebar);
